/**
 * Awareness → Spirit Balls conversion (`docs/09` §I.5).
 *
 *   spiritBalls = floor( sqrt(awarenesses / conversionBase[epoch]) )
 *
 * The square root is deliberate: it makes early conversions feel modest and
 * rewards patience. It also discourages rapid-fire crystallization spam — the
 * marginal return on each extra awareness shrinks.
 */
import { getConversionBase, getEpochThreshold, getNextEpoch } from './constants.js';
import type { CrystallizationPreview, Epoch } from './types.js';

/**
 * How many Spirit Balls the player would get if they crystallized right now,
 * and how much awareness it would consume.
 *
 * For the alpha we consume ALL awarenesses on a crystallization (Cookie-Clicker
 * style). A future refinement could let the player choose a partial amount.
 */
export function previewCrystallization(awarenesses: number, epoch: Epoch): CrystallizationPreview {
  if (awarenesses < 0 || !Number.isFinite(awarenesses)) {
    return { awarenessesToSpend: 0, spiritBallsToGain: 0 };
  }
  const base = getConversionBase(epoch);
  const ratio = awarenesses / base;
  if (ratio <= 0) {
    return { awarenessesToSpend: 0, spiritBallsToGain: 0 };
  }
  const spiritBalls = Math.floor(Math.sqrt(ratio));
  if (spiritBalls <= 0) {
    return { awarenessesToSpend: 0, spiritBallsToGain: 0 };
  }
  return {
    awarenessesToSpend: awarenesses,
    spiritBallsToGain: spiritBalls,
  };
}

/**
 * Check whether the player can advance to the next epoch given a balance.
 * The threshold lives on the CURRENT epoch index — see `EPOCH_THRESHOLDS`.
 */
export function canAdvanceEpoch(spiritBalls: number, fromEpoch: Epoch): boolean {
  const next = getNextEpoch(fromEpoch);
  if (next === null) {
    return false; // already at epoch 12; prestige is a separate flow
  }
  return spiritBalls >= getEpochThreshold(fromEpoch);
}

/**
 * Compute the spirit-ball amount required to reach the next epoch from the current
 * balance. Returns 0 when already qualified, and `Infinity` from epoch 12 (use
 * the prestige flow instead).
 */
export function spiritBallsToNextEpoch(spiritBalls: number, fromEpoch: Epoch): number {
  const next = getNextEpoch(fromEpoch);
  if (next === null) {
    return Number.POSITIVE_INFINITY;
  }
  const threshold = getEpochThreshold(fromEpoch);
  return Math.max(0, threshold - spiritBalls);
}

/**
 * Прогресс к следующему «шагу» кристаллизации.
 *
 * Формула conversion: spiritBalls = floor(sqrt(awarenesses / base)).
 * Это значит «ступени» лежат в N² × base:
 *   epoch 1 (base=100): 100 → 1 шар, 400 → 2 шара, 900 → 3, 1600 → 4, …
 *
 * Эта функция возвращает данные для progress-индикатора в кнопке:
 *   - currentTier: сколько шаров игрок получит ПРЯМО СЕЙЧАС (0 = ещё нет)
 *   - nextTier: до какого количества шаров мы целимся (currentTier + 1)
 *   - awarenessesAtPrevTier: пороговое значение awareness для currentTier
 *   - awarenessesAtNextTier: пороговое значение awareness для nextTier
 *   - awarenessesUntilNext: сколько ещё надо awareness до nextTier
 *   - progress01: 0..1 — насколько игрок между предыдущим и следующим tier
 *
 * JB 2026-05-25: кнопка кристаллизации заполняется по мере приближения к
 * каждой следующей ступени. Игрок видит progress bar + текст «до
 * следующего: N ос.».
 */
export interface CrystallizationTierProgress {
  readonly currentTier: number;
  readonly nextTier: number;
  readonly awarenessesAtPrevTier: number;
  readonly awarenessesAtNextTier: number;
  readonly awarenessesUntilNext: number;
  /** 0..1 — линейная позиция в окне [prev, next]. */
  readonly progress01: number;
}

export function crystallizationTierProgress(
  awarenesses: number,
  epoch: Epoch,
): CrystallizationTierProgress {
  if (awarenesses < 0 || !Number.isFinite(awarenesses)) {
    const base = getConversionBase(epoch);
    return {
      currentTier: 0,
      nextTier: 1,
      awarenessesAtPrevTier: 0,
      awarenessesAtNextTier: base,
      awarenessesUntilNext: base,
      progress01: 0,
    };
  }
  const base = getConversionBase(epoch);
  // Текущий tier = floor(sqrt(awarenesses / base)).
  const ratio = awarenesses / base;
  const currentTier = ratio > 0 ? Math.floor(Math.sqrt(ratio)) : 0;
  const nextTier = currentTier + 1;
  const awarenessesAtPrevTier = currentTier * currentTier * base;
  const awarenessesAtNextTier = nextTier * nextTier * base;
  const windowSize = awarenessesAtNextTier - awarenessesAtPrevTier;
  const within = Math.max(0, awarenesses - awarenessesAtPrevTier);
  const progress01 = windowSize > 0 ? Math.min(1, within / windowSize) : 0;
  const awarenessesUntilNext = Math.max(0, awarenessesAtNextTier - awarenesses);
  return {
    currentTier,
    nextTier,
    awarenessesAtPrevTier,
    awarenessesAtNextTier,
    awarenessesUntilNext,
    progress01,
  };
}
